import { Link, Route, Routes, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";

import { KindPicker, ReimbursementEditor, StatusBadge, errorMessage } from "./ReimbursementEntryPage";
import {
  fetchMyReimbursements,
  formatReimbursementMoney,
  reimbursementKindLabel,
} from "./reimbursementApi";

function ReimbursementList(): JSX.Element {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ["personal", "reimbursements"],
    queryFn: fetchMyReimbursements,
  });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PageTitleWithHelp hoverHint="Submit expense, per diem, mileage, and wellness reimbursements. Accounting reviews them here in the hub.">
          Reimbursements
        </PageTitleWithHelp>
        <Button type="button" className="min-h-12" onClick={() => navigate("/personal/reimbursements/new")}>
          New request
        </Button>
      </div>
      {query.isLoading ? <p className="text-sm text-slate-600">Loading your requests…</p> : null}
      {query.error ? <p className="text-sm text-red-700">{errorMessage(query.error)}</p> : null}
      {query.data && query.data.length === 0 ? (
        <Card className="p-5 text-sm text-slate-600">You have not submitted a reimbursement yet.</Card>
      ) : null}
      <div className="space-y-3">
        {(query.data ?? []).map((row) => (
          <Link
            key={row.id}
            to={`/personal/reimbursements/${row.id}`}
            className="block rounded-lg border border-slate-200 bg-white p-4 shadow-sm hover:border-slate-300"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-base font-medium text-slate-900">{reimbursementKindLabel(row.kind)}</div>
                <div className="mt-1 text-sm text-slate-600">
                  {row.startOn ?? row.incurredOn ?? "No date yet"}
                  {row.endOn && row.endOn !== row.startOn ? ` to ${row.endOn}` : ""}
                </div>
              </div>
              <div className="text-right">
                <StatusBadge status={row.status} />
                <div className="mt-2 text-sm font-medium tabular-nums text-slate-900">
                  {formatReimbursementMoney(row.amountPaid ?? row.amountRequested) || "—"}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function ReimbursementsPage(): JSX.Element {
  return (
    <Routes>
      <Route index element={<ReimbursementList />} />
      <Route path="new" element={<KindPicker />} />
      <Route path=":id" element={<ReimbursementEditor />} />
    </Routes>
  );
}
